import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  CircularProgress,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  SportsEsports as SportsEsportsIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../api';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill-new';
import {
  QUILL_TOOLBAR_CONTAINER,
  QUILL_FORMATS,
  plainTextLength,
} from '../../../utils/quillQuestionEditorConfig';

const GAME_CONTENT_QUILL_MODULES = {
  toolbar: {
    container: QUILL_TOOLBAR_CONTAINER,
    handlers: {
      image: function gameContentQuillImageHandler() {
        const quill = this.quill;
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/png,image/jpeg,image/webp');
        input.click();
        input.onchange = async () => {
          const file = input.files?.[0];
          if (!file) return;
          const formData = new FormData();
          formData.append('image', file);
          try {
            const res = await api.post('admin/game-contents/upload-image', formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            const url = res.data?.url;
            if (!url) {
              toast.error('No image URL returned');
              return;
            }
            const range = quill.getSelection(true) || { index: quill.getLength() };
            quill.insertEmbed(range.index, 'image', url, 'user');
            quill.setSelection(range.index + 1);
          } catch {
            toast.error('Image upload failed');
          }
        };
      },
    },
  },
};

const questionQuillBoxSx = {
  '& .quill': { background: '#fff', borderRadius: 1 },
  '& .ql-container': {
    minHeight: '140px',
    fontSize: '14px',
    borderRadius: '0 0 4px 4px',
  },
  '& .ql-toolbar': {
    borderRadius: '4px 4px 0 0',
    background: '#f9f9f9',
    flexWrap: 'wrap',
  },
  '& .ql-editor': { minHeight: '140px' },
};

const DEFAULT_OPTIONS = ['', '', '', ''];

const QUESTION_TYPES = {
  multiple_choice: 'Multiple choice',
  short_answer: 'Short answer',
  clock_set: 'Set the clock',
  clock_read: 'Read the clock',
  clock_match: 'Match the clock',
};

const MINUTE_OPTIONS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
const HOUR_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const formatTimeForClock = (hours, minutes) => {
  const h = hours % 12 || 12;
  const m = String(minutes).padStart(2, '0');
  return `${h}:${m}`;
};

const GameContentForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [gameTypes, setGameTypes] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [gameType, setGameType] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [topicId, setTopicId] = useState('');
  const [title, setTitle] = useState('');
  const [questionType, setQuestionType] = useState('multiple_choice');
  const [question, setQuestion] = useState('');
  const [questionImageUrl, setQuestionImageUrl] = useState('');
  const [options, setOptions] = useState([...DEFAULT_OPTIONS]);
  const [optionImages, setOptionImages] = useState(Array(DEFAULT_OPTIONS.length).fill(''));
  const [correctAnswerIndex, setCorrectAnswerIndex] = useState(0);
  const [shortAnswer, setShortAnswer] = useState('');
  const [shortAnswerImageUrl, setShortAnswerImageUrl] = useState('');
  const [clockInstruction, setClockInstruction] = useState('Set time on clock by moving hour/minute hand');
  const [clockTargetHours, setClockTargetHours] = useState(2);
  const [clockTargetMinutes, setClockTargetMinutes] = useState(20);
  const [clockReadHours, setClockReadHours] = useState(11);
  const [clockReadMinutes, setClockReadMinutes] = useState(20);
  const [clockReadOptions, setClockReadOptions] = useState(['11:20', '11:35', '11:05', '10:35']);
  const [clockReadCorrectIndex, setClockReadCorrectIndex] = useState(0);
  const [clockMatchTargetHours, setClockMatchTargetHours] = useState(11);
  const [clockMatchTargetMinutes, setClockMatchTargetMinutes] = useState(20);
  const [clockMatchDistractors, setClockMatchDistractors] = useState([
    { hours: 11, minutes: 35 },
    { hours: 11, minutes: 5 },
    { hours: 10, minutes: 35 },
  ]);
  const [explanation, setExplanation] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [contentJson, setContentJson] = useState('{}');
  const [difficulty, setDifficulty] = useState('normal');
  const [sortOrder, setSortOrder] = useState(0);
  const [status, setStatus] = useState(1);

  const questionImageInputRef = useRef(null);
  const optionImageInputRef = useRef(null);
  const answerImageInputRef = useRef(null);

  const uploadImageAndSet = async (file, setter) => {
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Please select an image file (JPG, PNG, GIF, WebP)');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2 MB');
      return;
    }
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await api.post('admin/game-contents/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.url) {
        setter(res.data.url);
        toast.success('Image uploaded');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Upload failed');
    }
  };

  useEffect(() => {
    api.get('admin/game-contents/game-types').then((r) => {
      if (r.data?.success && r.data?.data) setGameTypes(r.data.data);
    }).catch(() => {});
    api.get('admin/game-contents/subjects').then((r) => {
      if (r.data?.success && r.data?.data) setSubjects(r.data.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!subjectId) {
      setTopics([]);
      setTopicId('');
      return;
    }
    api.get('admin/game-contents/topics', { params: { subject_id: subjectId } })
      .then((r) => {
        if (r.data?.success && r.data?.data) setTopics(r.data.data);
        else setTopics([]);
      })
      .catch(() => setTopics([]));
    setTopicId('');
  }, [subjectId]);

  useEffect(() => {
    if (!isEdit) return;
    setFetching(true);
    api.get(`admin/game-contents/${id}`)
      .then((r) => {
        if (r.data?.success && r.data?.data) {
          const d = r.data.data;
          setGameType(d.game_type ?? '');
          setSubjectId(d.subject_id ?? '');
          setTopicId(d.topic_id ?? '');
          setTitle(d.title ?? '');
          setContentJson(d.content ? JSON.stringify(d.content, null, 2) : '{}');
          setDifficulty(d.difficulty ?? 'normal');
          setSortOrder(d.sort_order ?? 0);
          setStatus(d.status ?? 1);
          const c = d.content;
          if (c && typeof c === 'object') {
            setExplanation(c.explanation ?? '');
            setQuestionImageUrl(c.question_image ?? '');
            setShortAnswerImageUrl(c.answer_image ?? '');
            if (c.mode === 'set' || c.question_type === 'clock_set') {
              setQuestionType('clock_set');
              setClockInstruction(c.instruction ?? 'Set time on clock by moving hour/minute hand');
              setClockTargetHours(c.targetHours ?? 2);
              setClockTargetMinutes(c.targetMinutes ?? 20);
            } else if (c.mode === 'read' || c.question_type === 'clock_read') {
              setQuestionType('clock_read');
              setClockReadHours(c.hours ?? 11);
              setClockReadMinutes(c.minutes ?? 20);
              const opts = Array.isArray(c.options) ? c.options.map(String) : ['11:20', '11:35', '11:05', '10:35'];
              setClockReadOptions(opts.length >= 4 ? opts.slice(0, 4) : [...opts, ...Array(4 - opts.length).fill('')]);
              const ans = c.answer;
              const idx = typeof ans === 'number' ? ans : opts.indexOf(String(ans));
              setClockReadCorrectIndex(idx >= 0 && idx < opts.length ? idx : 0);
            } else if (c.mode === 'match' || c.question_type === 'clock_match') {
              setQuestionType('clock_match');
              setClockMatchTargetHours(c.targetHours ?? 11);
              setClockMatchTargetMinutes(c.targetMinutes ?? 20);
              const co = c.clockOptions || [];
              if (co.length >= 4) {
                setClockMatchDistractors([
                  { hours: co[1].hours ?? 11, minutes: co[1].minutes ?? 35 },
                  { hours: co[2].hours ?? 11, minutes: co[2].minutes ?? 5 },
                  { hours: co[3].hours ?? 10, minutes: co[3].minutes ?? 35 },
                ]);
              }
            } else if (c.question_type === 'short_answer' || !Array.isArray(c.options) || c.options.length === 0) {
              setQuestionType('short_answer');
              setQuestion(c.question ?? '');
              setShortAnswer(c.answer ?? '');
            } else {
              setQuestionType('multiple_choice');
              setQuestion(c.question ?? '');
              const opts = Array.isArray(c.options) ? c.options : [];
              const texts = opts.length ? opts.map((o) => (typeof o === 'object' && o !== null && 'text' in o ? String(o.text) : String(o))) : [...DEFAULT_OPTIONS];
              const imgs = opts.length ? opts.map((o) => (typeof o === 'object' && o !== null && 'image' in o ? String(o.image || '') : '')) : [];
              setOptions(texts.length ? texts : [...DEFAULT_OPTIONS]);
              setOptionImages(texts.length ? imgs.length === texts.length ? imgs : [...imgs, ...Array(texts.length - imgs.length).fill('')] : Array(DEFAULT_OPTIONS.length).fill(''));
              const ans = c.answer;
              let idx = 0;
              if (typeof ans === 'number' && ans >= 0 && ans < texts.length) idx = ans;
              else {
                const match = texts.findIndex((t) => String(t) === String(ans));
                if (match >= 0) idx = match;
                else if (opts.some((o) => typeof o === 'object' && o?.text != null)) {
                  const objMatch = opts.findIndex((o) => typeof o === 'object' && String(o?.text) === String(ans));
                  if (objMatch >= 0) idx = objMatch;
                }
              }
              setCorrectAnswerIndex(idx >= 0 && idx < texts.length ? idx : 0);
            }
          }
        }
      })
      .catch(() => toast.error('Failed to load game content'))
      .finally(() => setFetching(false));
  }, [id, isEdit]);

  useEffect(() => {
    if (!isEdit || !subjectId) return;
    api.get('admin/game-contents/topics', { params: { subject_id: subjectId } })
      .then((r) => {
        if (r.data?.success && r.data?.data) setTopics(r.data.data);
        else setTopics([]);
      })
      .catch(() => setTopics([]));
  }, [isEdit, subjectId]);

  const handleOptionChange = (index, value) => {
    const next = [...options];
    next[index] = value;
    setOptions(next);
  };

  const handleAddOption = () => {
    setOptions([...options, '']);
    setOptionImages([...optionImages, '']);
  };

  const handleRemoveOption = (index) => {
    if (options.length <= 1) return;
    const next = options.filter((_, i) => i !== index);
    const nextImages = optionImages.filter((_, i) => i !== index);
    setOptions(next);
    setOptionImages(nextImages.length ? nextImages : Array(next.length).fill(''));
    if (correctAnswerIndex >= next.length) {
      setCorrectAnswerIndex(Math.max(0, next.length - 1));
    } else if (correctAnswerIndex > index) {
      setCorrectAnswerIndex(correctAnswerIndex - 1);
    }
  };

  const setOptionImage = (index, url) => {
    const next = [...optionImages];
    while (next.length <= index) next.push('');
    next[index] = url;
    setOptionImages(next);
  };

  const handleClockReadOptionChange = (index, value) => {
    const next = [...clockReadOptions];
    next[index] = value;
    setClockReadOptions(next);
  };

  const handleClockMatchDistractorChange = (index, field, value) => {
    const next = [...clockMatchDistractors];
    next[index] = { ...next[index], [field]: Number(value) };
    setClockMatchDistractors(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!gameType.trim()) {
      toast.error('Please select or enter game type');
      return;
    }
    let content = null;
    if (showAdvanced) {
      if (contentJson.trim()) {
        try {
          content = JSON.parse(contentJson);
        } catch {
          toast.error('Content must be valid JSON');
          return;
        }
      }
    } else {
      if (questionType === 'clock_set') {
        content = {
          question_type: 'clock_set',
          mode: 'set',
          instruction: clockInstruction.trim() || 'Set time on clock by moving hour/minute hand',
          targetHours: Number(clockTargetHours) || 2,
          targetMinutes: Number(clockTargetMinutes) || 0,
          answer: formatTimeForClock(Number(clockTargetHours) || 2, Number(clockTargetMinutes) || 0),
          explanation: explanation.trim() || undefined,
        };
      } else if (questionType === 'clock_read') {
        const readOpts = clockReadOptions.map((o) => String(o).trim()).filter(Boolean);
        content = {
          question_type: 'clock_read',
          mode: 'read',
          hours: Number(clockReadHours) || 12,
          minutes: Number(clockReadMinutes) ?? 0,
          options: readOpts.length >= 4 ? readOpts : [...readOpts, ...Array(4 - readOpts.length).fill(formatTimeForClock(clockReadHours, clockReadMinutes))],
          answer: readOpts[clockReadCorrectIndex] ?? formatTimeForClock(clockReadHours, clockReadMinutes),
          explanation: explanation.trim() || undefined,
        };
      } else if (questionType === 'clock_match') {
        const correct = { hours: Number(clockMatchTargetHours) || 12, minutes: Number(clockMatchTargetMinutes) ?? 0 };
        const dist = clockMatchDistractors.map((d) => ({ hours: Number(d.hours) || 12, minutes: Number(d.minutes) ?? 0 }));
        content = {
          question_type: 'clock_match',
          mode: 'match',
          targetHours: correct.hours,
          targetMinutes: correct.minutes,
          clockOptions: [correct, ...dist],
          answer: '0',
          explanation: explanation.trim() || undefined,
        };
      } else if (questionType === 'short_answer') {
        content = {
          question_type: 'short_answer',
          question: plainTextLength(question) === 0 ? '' : String(question).trim(),
          ...(questionImageUrl.trim() && { question_image: questionImageUrl.trim() }),
          answer: shortAnswer.trim() || '',
          ...(shortAnswerImageUrl.trim() && { answer_image: shortAnswerImageUrl.trim() }),
          explanation: explanation.trim() || undefined,
        };
      } else {
        const opts = options.map((o) => String(o).trim()).filter(Boolean);
        const hasOptionImages = optionImages.some((url, i) => url && i < options.length);
        const optionsPayload = hasOptionImages
          ? options.map((text, i) => ({
              text: String(text).trim(),
              ...(optionImages[i] && { image: optionImages[i] }),
            }))
          : opts;
        const correctText = opts[correctAnswerIndex] ?? opts[0] ?? '';
        content = {
          question: plainTextLength(question) === 0 ? '' : String(question).trim(),
          ...(questionImageUrl.trim() && { question_image: questionImageUrl.trim() }),
          options: optionsPayload,
          answer: hasOptionImages ? (optionsPayload[correctAnswerIndex]?.text ?? correctText) : correctText,
          explanation: explanation.trim() || undefined,
        };
      }
      if (content.explanation === undefined) delete content.explanation;
    }
    setLoading(true);
    try {
      const payload = {
        game_type: gameType.trim(),
        subject_id: subjectId || null,
        topic_id: topicId || null,
        title: title.trim() || null,
        content,
        difficulty,
        sort_order: Number(sortOrder) || 0,
        status: Number(status),
      };
      if (isEdit) {
        await api.put(`admin/game-contents/${id}`, payload);
        toast.success('Game content updated');
      } else {
        await api.post('admin/game-contents', payload);
        toast.success('Game content created');
      }
      navigate('/admin/game-contents');
    } catch (err) {
      const msg = err.response?.data?.message || (err.response?.data?.errors ? Object.values(err.response.data.errors).flat().join(' ') : 'Failed to save');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/game-contents')}>
          Back
        </Button>
        <Box sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 2,
          background: 'linear-gradient(135deg, #5e35b1 0%, #7e57c2 100%)',
          borderRadius: '25px',
          px: 3,
          py: 1,
          boxShadow: '0 4px 15px rgba(94, 53, 177, 0.3)',
        }}>
          <SportsEsportsIcon sx={{ fontSize: '24px', color: 'white' }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
            {isEdit ? 'Edit Game Content' : 'Add Game Content'}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Game type</InputLabel>
                <Select
                  value={gameType}
                  onChange={(e) => setGameType(e.target.value)}
                  label="Game type"
                >
                  <MenuItem value="">Select game type</MenuItem>
                  {gameTypes.map((key) => (
                    <MenuItem key={key} value={key}>{key}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Difficulty</InputLabel>
                <Select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} label="Difficulty">
                  <MenuItem value="easy">Easy</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="hard">Hard</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Subject</InputLabel>
                <Select value={subjectId} onChange={(e) => setSubjectId(e.target.value)} label="Subject">
                  <MenuItem value="">None</MenuItem>
                  {subjects.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={!subjectId}>
                <InputLabel>Topic</InputLabel>
                <Select value={topicId} onChange={(e) => setTopicId(e.target.value)} label="Topic">
                  <MenuItem value="">None</MenuItem>
                  {topics.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Question type</InputLabel>
                <Select
                  value={questionType}
                  onChange={(e) => setQuestionType(e.target.value)}
                  label="Question type"
                >
                  <MenuItem value="multiple_choice">{QUESTION_TYPES.multiple_choice}</MenuItem>
                  <MenuItem value="short_answer">{QUESTION_TYPES.short_answer}</MenuItem>
                  <MenuItem value="clock_set">{QUESTION_TYPES.clock_set}</MenuItem>
                  <MenuItem value="clock_read">{QUESTION_TYPES.clock_read}</MenuItem>
                  <MenuItem value="clock_match">{QUESTION_TYPES.clock_match}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {questionType === 'clock_set' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Instruction"
                    value={clockInstruction}
                    onChange={(e) => setClockInstruction(e.target.value)}
                    placeholder="Set time on clock by moving hour/minute hand"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Target hour</InputLabel>
                    <Select
                      value={clockTargetHours}
                      onChange={(e) => setClockTargetHours(Number(e.target.value))}
                      label="Target hour"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((h) => (
                        <MenuItem key={h} value={h}>{h}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Target minute</InputLabel>
                    <Select
                      value={clockTargetMinutes}
                      onChange={(e) => setClockTargetMinutes(Number(e.target.value))}
                      label="Target minute"
                    >
                      {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
                        <MenuItem key={m} value={m}>{String(m).padStart(2, '0')}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fff8e1', border: '1px solid #ffc107', display: 'inline-block' }}>
                    <Typography variant="body2" color="text.secondary">Preview: Set to: </Typography>
                    <Typography variant="h6" fontWeight="bold" color="#f57c00">
                      {formatTimeForClock(clockTargetHours, clockTargetMinutes)}
                    </Typography>
                  </Box>
                </Grid>
              </>
            )}

            {questionType === 'clock_read' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Time shown on the clock (students pick the matching time from options below)</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Clock hour</InputLabel>
                    <Select value={clockReadHours} onChange={(e) => setClockReadHours(Number(e.target.value))} label="Clock hour">
                      {HOUR_OPTIONS.map((h) => (
                        <MenuItem key={h} value={h}>{h}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Clock minute</InputLabel>
                    <Select value={clockReadMinutes} onChange={(e) => setClockReadMinutes(Number(e.target.value))} label="Clock minute">
                      {MINUTE_OPTIONS.map((m) => (
                        <MenuItem key={m} value={m}>{String(m).padStart(2, '0')}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Four time options (one must match the clock)</Typography>
                  {clockReadOptions.map((opt, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label={`Option ${index + 1}`}
                        value={opt}
                        onChange={(e) => handleClockReadOptionChange(index, e.target.value)}
                        placeholder="e.g. 11:20"
                      />
                    </Box>
                  ))}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Correct answer (which option matches the clock)</InputLabel>
                    <Select
                      value={clockReadCorrectIndex}
                      onChange={(e) => setClockReadCorrectIndex(Number(e.target.value))}
                      label="Correct answer (which option matches the clock)"
                    >
                      {clockReadOptions.map((_, index) => (
                        <MenuItem key={index} value={index}>
                          Option {index + 1}: {clockReadOptions[index] || '—'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#e3f2fd', border: '1px solid #1976d2', display: 'inline-block' }}>
                    <Typography variant="body2" color="text.secondary">Preview: Clock shows </Typography>
                    <Typography variant="h6" fontWeight="bold" color="#1976d2">
                      {formatTimeForClock(clockReadHours, clockReadMinutes)}
                    </Typography>
                  </Box>
                </Grid>
              </>
            )}

            {questionType === 'clock_match' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Target time shown to student (they choose the clock that matches)</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Target hour</InputLabel>
                    <Select value={clockMatchTargetHours} onChange={(e) => setClockMatchTargetHours(Number(e.target.value))} label="Target hour">
                      {HOUR_OPTIONS.map((h) => (
                        <MenuItem key={h} value={h}>{h}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Target minute</InputLabel>
                    <Select value={clockMatchTargetMinutes} onChange={(e) => setClockMatchTargetMinutes(Number(e.target.value))} label="Target minute">
                      {MINUTE_OPTIONS.map((m) => (
                        <MenuItem key={m} value={m}>{String(m).padStart(2, '0')}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Three wrong clocks (distractors)</Typography>
                  {clockMatchDistractors.map((_, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
                      <Typography variant="body2">Clock {index + 2}:</Typography>
                      <FormControl size="small" sx={{ minWidth: 80 }}>
                        <InputLabel>Hour</InputLabel>
                        <Select
                          value={clockMatchDistractors[index].hours}
                          onChange={(e) => handleClockMatchDistractorChange(index, 'hours', e.target.value)}
                          label="Hour"
                        >
                          {HOUR_OPTIONS.map((h) => (
                            <MenuItem key={h} value={h}>{h}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 80 }}>
                        <InputLabel>Min</InputLabel>
                        <Select
                          value={clockMatchDistractors[index].minutes}
                          onChange={(e) => handleClockMatchDistractorChange(index, 'minutes', e.target.value)}
                          label="Min"
                        >
                          {MINUTE_OPTIONS.map((m) => (
                            <MenuItem key={m} value={m}>{String(m).padStart(2, '0')}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  ))}
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#e8f5e9', border: '1px solid #4caf50', display: 'inline-block' }}>
                    <Typography variant="body2" color="text.secondary">Preview: &quot;Choose the correct clock&quot; for </Typography>
                    <Typography variant="h6" fontWeight="bold" color="#2e7d32">
                      {formatTimeForClock(clockMatchTargetHours, clockMatchTargetMinutes)}
                    </Typography>
                  </Box>
                </Grid>
              </>
            )}

            {questionType === 'short_answer' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Question
                  </Typography>
                  <Box sx={questionQuillBoxSx}>
                    <ReactQuill
                      theme="snow"
                      value={question}
                      onChange={setQuestion}
                      modules={GAME_CONTENT_QUILL_MODULES}
                      formats={QUILL_FORMATS}
                      placeholder="Enter the question (formatting, lists, images…)"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Question image / icon / symbol (optional)</Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                      size="small"
                      placeholder="Image URL or upload below"
                      value={questionImageUrl}
                      onChange={(e) => setQuestionImageUrl(e.target.value)}
                      sx={{ flex: 1, minWidth: 200 }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => questionImageInputRef.current?.click()}
                    >
                      Upload
                    </Button>
                    {questionImageUrl && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <img src={questionImageUrl} alt="Question" style={{ maxHeight: 40, maxWidth: 80, objectFit: 'contain' }} />
                        <IconButton size="small" onClick={() => setQuestionImageUrl('')} title="Clear image">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                  <input
                    type="file"
                    ref={questionImageInputRef}
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadImageAndSet(file, setQuestionImageUrl);
                      e.target.value = '';
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Answer"
                    value={shortAnswer}
                    onChange={(e) => setShortAnswer(e.target.value)}
                    placeholder="Correct answer (short text)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Answer image / icon / symbol (optional)</Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                      size="small"
                      placeholder="Image URL or upload below"
                      value={shortAnswerImageUrl}
                      onChange={(e) => setShortAnswerImageUrl(e.target.value)}
                      sx={{ flex: 1, minWidth: 200 }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => answerImageInputRef.current?.click()}
                    >
                      Upload
                    </Button>
                    {shortAnswerImageUrl && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <img src={shortAnswerImageUrl} alt="Answer" style={{ maxHeight: 40, maxWidth: 80, objectFit: 'contain' }} />
                        <IconButton size="small" onClick={() => setShortAnswerImageUrl('')} title="Clear image">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                  <input
                    type="file"
                    ref={answerImageInputRef}
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadImageAndSet(file, setShortAnswerImageUrl);
                      e.target.value = '';
                    }}
                  />
                </Grid>
              </>
            )}

            {questionType === 'multiple_choice' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Question
                  </Typography>
                  <Box sx={questionQuillBoxSx}>
                    <ReactQuill
                      theme="snow"
                      value={question}
                      onChange={setQuestion}
                      modules={GAME_CONTENT_QUILL_MODULES}
                      formats={QUILL_FORMATS}
                      placeholder="Enter the question (formatting, lists, images…)"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Question image / icon / symbol (optional)</Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                      size="small"
                      placeholder="Image URL or upload below"
                      value={questionImageUrl}
                      onChange={(e) => setQuestionImageUrl(e.target.value)}
                      sx={{ flex: 1, minWidth: 200 }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => questionImageInputRef.current?.click()}
                    >
                      Upload
                    </Button>
                    <input
                      type="file"
                      ref={questionImageInputRef}
                      accept="image/*"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) uploadImageAndSet(file, setQuestionImageUrl);
                        e.target.value = '';
                      }}
                    />
                    {questionImageUrl && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <img src={questionImageUrl} alt="Question" style={{ maxHeight: 40, maxWidth: 80, objectFit: 'contain' }} />
                        <IconButton size="small" onClick={() => setQuestionImageUrl('')} title="Clear image">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Options (leave blank to remove)</Typography>
                  {options.map((opt, index) => (
                    <Box key={index} sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label={`Option ${index + 1} text`}
                          value={opt}
                          onChange={(e) => handleOptionChange(index, e.target.value)}
                          placeholder={`Option ${index + 1}`}
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveOption(index)}
                          disabled={options.length <= 1}
                          title="Remove option"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                        <ImageIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <TextField
                          size="small"
                          placeholder="Image URL or upload"
                          value={optionImages[index] ?? ''}
                          onChange={(e) => setOptionImage(index, e.target.value)}
                          sx={{ flex: 1, minWidth: 160 }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CloudUploadIcon />}
                          onClick={() => {
                            const input = optionImageInputRef.current;
                            if (input) {
                              input.setAttribute('data-option-index', String(index));
                              input.click();
                            }
                          }}
                        >
                          Upload
                        </Button>
                        {(optionImages[index] ?? '') && (
                          <>
                            <img src={optionImages[index]} alt="" style={{ maxHeight: 32, maxWidth: 48, objectFit: 'contain' }} />
                            <IconButton size="small" onClick={() => setOptionImage(index, '')} title="Clear image">
                              <DeleteIcon />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </Box>
                  ))}
                  <input
                    type="file"
                    ref={optionImageInputRef}
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      const idx = parseInt(optionImageInputRef.current?.getAttribute('data-option-index') ?? '0', 10);
                      if (file && !Number.isNaN(idx)) uploadImageAndSet(file, (url) => setOptionImage(idx, url));
                      e.target.value = '';
                    }}
                  />
                  <Button startIcon={<AddIcon />} size="small" onClick={handleAddOption} sx={{ mt: 0.5 }}>
                    Add option
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Correct answer</InputLabel>
                    <Select
                      value={correctAnswerIndex}
                      onChange={(e) => setCorrectAnswerIndex(Number(e.target.value))}
                      label="Correct answer"
                    >
                      {options.map((_, index) => (
                        <MenuItem key={index} value={index}>
                          Option {index + 1}{options[index] ? `: ${options[index].slice(0, 40)}${options[index].length > 40 ? '…' : ''}` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Explanation (optional)"
                value={explanation}
                onChange={(e) => setExplanation(e.target.value)}
                placeholder="Explain the correct answer..."
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setShowAdvanced(!showAdvanced)}
                endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {showAdvanced ? 'Hide advanced' : 'Advanced: edit as JSON'}
              </Button>
              <Collapse in={showAdvanced}>
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  label="Content (JSON)"
                  value={contentJson}
                  onChange={(e) => setContentJson(e.target.value)}
                  placeholder='{"question": "...", "options": [], "answer": "..."}'
                  helperText="Only used when advanced is open. Otherwise question, options and answer above are saved."
                  sx={{ mt: 2 }}
                />
              </Collapse>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField type="number" label="Sort order" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} inputProps={{ min: 0 }} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={status} onChange={(e) => setStatus(Number(e.target.value))} label="Status">
                  <MenuItem value={1}>Inactive</MenuItem>
                  <MenuItem value={2}>Active</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" disabled={loading} sx={{ bgcolor: '#5e35b1', '&:hover': { bgcolor: '#4527a0' } }}>
                {loading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default GameContentForm;
